<template>
  <div class="user-profile">
    <div class="user-profile__layout">
      <div class="user-profile__fields">
        <v-expansion-panels
          v-model="panelStates"
          class="user-profile__panels"
          variant="accordion"
          flat
        >
          <v-expansion-panel
            v-for="field in items"
            :key="field.alias"
            class="user-profile__panel"
            :disabled="field.disabled"
          >
            <v-expansion-panel-title
              class="user-profile__title"
              :hideActions="field.disabled"
              :disabled="field.disabled"
              expandIcon="mdi-pencil-outline"
              collapseIcon="mdi-close"
            >
              <div class="user-profile__row">
                <div class="user-profile__meta">
                  <div class="user-profile__label">
                    {{ field.title }}
                    <span
                      v-if="field.isMandatory"
                      class="user-profile__required"
                    >*</span>
                  </div>
                  <div class="user-profile__value">
                    <span class="user-profile__value-text">
                      {{ formatFieldValue(field) }}
                    </span>
                    <FieldConfirmedStatusIcon
                      v-if="field.value"
                      :alias="field.alias"
                    />
                  </div>
                </div>
              </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text class="user-profile__edit">
              <UserProfileConfirmableField
                v-if="field.alias === 'email' && otpConfirm['email'] !== false"
                alias="email"
                :confirmMode="otpConfirm['email'] || 'optional'"
                :legacyOtpFlow="legacyOtpFlow"
                :disabled="!isEditEmail"
                @updated="fetchData"
              />
              <UserProfileConfirmableField
                v-else-if="field.alias === 'phone' && otpConfirm['phone'] !== false"
                alias="phone"
                :confirmMode="otpConfirm['phone'] || 'optional'"
                :legacyOtpFlow="legacyOtpFlow"
                :inputProperties="resultInputProperties"
                :disabled="!isEditPhone"
                @updated="fetchData"
              />
              <ChangePassword
                v-else-if="field.alias === 'password'"
                :confirmBy="passwordConfirmBy"
                :defaultValue="confirmDestination"
                :inputProperties="resultInputProperties"
                :canChangeValue="false"
              />
              <UserProfileEditField
                v-else
                :alias="field.alias"
                :inputProperties="resultInputProperties"
                :validateFn="getConditionForField(field.alias)"
                @updated="fetchData"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <UserProfileAvatar
        v-if="isAvatar"
        class="user-profile__avatar"
        :noEdit="!isEditAvatar"
      >
        <h2
          v-if="avatarTitle"
          class="user-profile__avatar-title"
        >
          {{ avatarTitle }}
        </h2>
      </UserProfileAvatar>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { UserProfileProps, defaults } from './UserProfile.ts';

import { FormField, assertFieldType } from '@/models/form-builder-interface';

import { useAccountPropertyCache, useEnrollSettings, useLocalization } from '@/composables';
import { useAccountPropertiesItem } from '@/composables/useAccountPropertiesItem';

import UserProfileAvatar from './base/UserProfileAvatar.vue';
import UserProfileConfirmableField from './base/UserProfileConfirmableField.vue';
import UserProfileEditField from './base/UserProfileEditField.vue';

import ChangePassword from './base/ChangePassword.vue';
import FieldConfirmedStatusIcon from './base/FieldConfirmedStatusIcon.vue';

import { apiErrorToString } from '@ui-api3-sdk/api/api3';
import { useAccountStore } from '@/pinia/useAccountStore';
import { usePropertiesStore } from '@/pinia/usePropertiesStore';
import { formatDate } from '@/utils/utils';
import { createEvalValidator } from '@/utils/eval-validator.ts';
import { fetchFullProperties } from '@/utils/fetch-full-properties-service';

const { lt, t } = useLocalization('uiUserProfile', [
  'unknown_error',
]);

const panelStates = ref<number | null>(null);

const props = withDefaults(defineProps<UserProfileProps>(), {
  id: undefined,
  editableFields: undefined,
  inputProperties: undefined,
  profileFields: () => defaults.profileFields,
  passwordConfirmBy: () => defaults.passwordConfirmBy,
  otpConfirm: () => defaults.otpConfirm,
  legacyOtpFlow: () => defaults.legacyOtpFlow,
});

const { inputProperties: resultInputProperties } = useEnrollSettings(props.inputProperties);

const isAvatar = computed(() => props.profileFields.includes('avatar_id'));

const isEditEmail = computed(() => props.editableFields?.includes('email'));
const isEditPhone = computed(() => props.editableFields?.includes('phone'));
const isEditAvatar = computed(() => props.editableFields?.includes('avatar_id'));

const prettyAccountId = ref('');
const { item: accountItem } = useAccountPropertiesItem();

const { presentable: currentEmail } = useAccountPropertyCache('email');
const { presentable: currentPhone } = useAccountPropertyCache('phone');

const confirmDestination = computed(() => {
  if (props.passwordConfirmBy === 'email') return currentEmail.value || '';
  if (props.passwordConfirmBy === 'phone') return currentPhone.value || '';
  return '';
});

const items = ref<FormField[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

const avatarTitle = ref('');

function formatFieldValue(field: FormField): string {
  if (field.alias === 'password') return '••••••••';
  if (field.value == null || field.value === '') return '—';
  return String(field.value);
}

async function refreshAvatarTitle() {
  const alias = props.overrideAccountTitle;
  if (!alias) {
    avatarTitle.value = prettyAccountId.value;
    return;
  }

  // Profile fields come as 'p.field'. Use account item shortcut.
  if (alias.startsWith('p.')) {
    const val = accountItem.value?.p(alias) || '';
    avatarTitle.value = val || prettyAccountId.value;
    return;
  }

  try {
    const full = await fetchFullProperties([alias]);
    const val = String(full?.[alias]?.presentable || '');
    avatarTitle.value = val || prettyAccountId.value;
  } catch (e) {
    avatarTitle.value = prettyAccountId.value;
  }
}

function getConditionForField(field: string) {

  const entries = props.conditions?.filter( (entry) => entry.field === field) || [];
  if (entries.length === 0) return undefined;

  const validators: Array<{ validator: (v:any) => boolean, text: string }> = [];

  entries.forEach( (entry) => {
    const validator = createEvalValidator(entry.condition);
    validators.push({
      validator,
      text: entry.text || t('inputs.err.invalid_value'),
    });
  });

  return (value: any) => {
    for (const { validator, text } of validators) {
      if (! validator(value)) return text;
    }
    return true;
  };

}


function resolveLanguageName(key: string | null | undefined): string | undefined {
  if (!key) return undefined;
  const propDef = usePropertiesStore().findPropertyDefinition('p', 'language_id');
  const fromDict = propDef?.dictionary?.dictionaryItems?.find((i: any) => i.key === key);
  if (fromDict) return fromDict.title;
  const fromList = usePropertiesStore().getLanguagesList().find((i) => i.key === key);
  return fromList?.value ?? key;
}

async function fetchData() {
  loading.value = true;
  const res: FormField[] = [];

  await usePropertiesStore().getPfAsync();

  if (props.profileFields.includes('language_id'))
    await usePropertiesStore().fetchLanguagesList();

  await useAccountStore().fetchAccount(); // force fetch account

  const currentAccount = await useAccountStore().getAccountAsync();
  const profile = currentAccount?.profile;

  if (! profile ) throw new Error('Could not get profile fields');

  prettyAccountId.value = currentAccount.idValue.presentable;
  await refreshAvatarTitle();

  try {

    props.profileFields.forEach( (alias) => {

      if (alias === 'avatar_id') return;

      const propType = usePropertiesStore().getPropertyType(alias, undefined, 'p');

      const cachedProp = useAccountPropertyCache(alias).prop;

      const value =
        alias === 'language_id' ?
          resolveLanguageName(cachedProp.value?.raw as string) :
          alias.includes('date') ?
            formatDate(cachedProp.value?.raw) :
            cachedProp.value?.presentable;

      res.push({
        title: propType.title,
        alias: propType.alias,
        value,
        disabled: ! props.editableFields?.includes(alias),
        isMandatory: propType.isMandatory,
        fieldType: assertFieldType(propType.fieldType),
      });

    });

    items.value = res;

  } catch (err) {
    error.value = apiErrorToString(err, lt('unknown_error'));
    console.error('fetchData', err);
  } finally {
    loading.value = false;
  }
}

onMounted( () => {
  fetchData();
});

watch(() => props.overrideAccountTitle, () => {
  refreshAvatarTitle();
});


</script>

<style scoped lang="scss">
.user-profile {
  width: 100%;

  &__layout {
    display: flex;
    flex-direction: column-reverse;
    gap: 2rem;
    align-items: stretch;

    @media (min-width: 960px) {
      flex-direction: row;
      align-items: flex-start;
      gap: 2.5rem;
    }
  }

  &__fields {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 40rem;
  }

  &__avatar {
    flex: 0 0 auto;
    align-self: center;

    @media (min-width: 960px) {
      align-self: flex-start;
      margin-top: 0.25rem;
    }
  }

  &__avatar-title {
    margin: 0.75rem 0 0;
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.35;
    text-align: center;
    word-break: break-word;
  }

  &__panels {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 0.75rem;
    overflow: hidden;
    background: rgb(var(--v-theme-surface));
  }

  &__panel {
    border-radius: 0 !important;

    & + & {
      border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
  }

  &__title {
    min-height: 4.25rem;
    padding-block: 0.875rem;
  }

  &__row {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    padding-inline-end: 0.5rem;
  }

  &__meta {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__label {
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.3;
    letter-spacing: 0.01em;
    color: rgba(var(--v-theme-on-surface), 0.55);
    overflow-wrap: anywhere;
  }

  &__required {
    color: rgb(var(--v-theme-error));
    font-weight: 700;
    margin-inline-start: 0.15rem;
  }

  &__value {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
  }

  &__value-text {
    font-size: 0.9375rem;
    font-weight: 500;
    line-height: 1.4;
    color: rgb(var(--v-theme-on-surface));
    overflow-wrap: anywhere;
  }

  &__edit {
    padding-top: 0.25rem;
    padding-bottom: 0.5rem;
  }
}

.user-profile__panels {
  :deep(.v-expansion-panel) {
    background: transparent;
  }

  :deep(.v-expansion-panel::after) {
    display: none;
  }

  :deep(.v-expansion-panel-title) {
    padding-inline: 1rem;
    gap: 0.75rem;
  }

  :deep(.v-expansion-panel-title__icon) {
    margin-inline-start: 0;
    color: rgba(var(--v-theme-on-surface), 0.45);
  }

  :deep(.v-expansion-panel-title:hover .v-expansion-panel-title__icon) {
    color: rgba(var(--v-theme-on-surface), 0.75);
  }

  :deep(.v-expansion-panel-title--disabled) {
    opacity: 1;
  }

  :deep(.v-expansion-panel-title--disabled .user-profile__value-text) {
    color: rgba(var(--v-theme-on-surface), 0.7);
  }

  :deep(.v-expansion-panel-text__wrapper) {
    padding-inline: 1rem;
    padding-bottom: 1rem;
  }

  :deep(.v-expansion-panel--active > .v-expansion-panel-title) {
    min-height: 4.25rem;
  }
}
</style>
